/**
 * What Home reads from the rest of the platform, as one value:
 * the findings assigned to you, regulatory updates newest first with the ones
 * still to review, the allowance, and the workflows with the ones failing.
 *
 * @module components/home/homeGlance.logic
 */
import type { WorkflowSummary } from "@amoreg/contracts";

import { modelSpendIsShowable, type ModelSpendStatus, type ShowableModelSpend } from "../../state/modelSpend";
import type { RegulatoryUpdate, RegulatoryUpdateSnapshot } from "../../state/policies";
import { pileOf } from "../regulation/monitor/publications";
import { severityRank, type FindingItem } from "../regulation/finding/findingItem";
import { daysUntil } from "./HomeAgenda.logic";

/** The Allowance card's figure: still loading, a capped allowance, no cap at all, or a read that failed. */
export type HomeAllowance =
  | { readonly kind: "loading" }
  | { readonly kind: "shown"; readonly spend: ShowableModelSpend }
  | { readonly kind: "unlimited"; readonly spentGbp: number }
  | { readonly kind: "unavailable" };

export interface HomeGlance {
  /** Open findings assigned to the signed-in person, most pressing first. */
  readonly mine: ReadonlyArray<FindingItem>;
  /** Newest first. */
  readonly updates: ReadonlyArray<RegulatoryUpdate>;
  readonly toReview: ReadonlyArray<RegulatoryUpdate>;
  readonly lastChecked: string | null;
  readonly allowance: HomeAllowance;
  readonly workflows: ReadonlyArray<WorkflowSummary>;
  /** The last run failed, or the schedule could not start one. */
  readonly failing: ReadonlyArray<WorkflowSummary>;
}


/** A finding's due day at noon, so the day never slips across a timezone; infinity when it has none. */
export const findingDueAt = (item: Pick<FindingItem, "dueDate">) =>
  item.dueDate ? Date.parse(`${item.dueDate}T12:00:00`) : Number.POSITIVE_INFINITY;

export const isFindingOverdue = (item: Pick<FindingItem, "dueDate">, now: number) => {
  const at = findingDueAt(item);
  return Number.isFinite(at) && daysUntil(at, now) < 0;
};

/** Open findings assigned to `me`: soonest due first (overdue leads), then most severe. */
export function selectMyFindings(findings: ReadonlyArray<FindingItem>, me: string | null): ReadonlyArray<FindingItem> {
  if (me === null) return [];
  return findings
    .filter((item) => item.assigneeId === me)
    .toSorted((a, b) => findingDueAt(a) - findingDueAt(b) || severityRank(b.severity) - severityRank(a.severity));
}

/** The Regulatory page's own rule: undecided, or the agent's "relevant" that no person has confirmed yet. */
export const isUpdateToReview = (update: RegulatoryUpdate) => pileOf(update) === "decide";

function homeAllowance(spend: { readonly loaded: boolean; readonly status: ModelSpendStatus | null }): HomeAllowance {
  if (!spend.loaded) return { kind: "loading" };
  if (modelSpendIsShowable(spend.status)) return { kind: "shown", spend: spend.status };
  if (spend.status?.state === "unlimited") return { kind: "unlimited", spentGbp: spend.status.spentGbp };
  return { kind: "unavailable" };
}

export const isWorkflowFailing = (workflow: Pick<WorkflowSummary, "lastRun" | "schedule">) =>
  workflow.lastRun?.status === "failed" || Boolean(workflow.schedule?.lastError);

export function buildHomeGlance(input: {
  readonly findings: ReadonlyArray<FindingItem>;
  readonly me: string | null;
  readonly updates: RegulatoryUpdateSnapshot | null;
  readonly spend: { readonly loaded: boolean; readonly status: ModelSpendStatus | null };
  readonly workflows: ReadonlyArray<WorkflowSummary>;
}): HomeGlance {
  const updates = (input.updates?.updates ?? []).toSorted((a, b) => Date.parse(b.detectedAt) - Date.parse(a.detectedAt));
  return {
    mine: selectMyFindings(input.findings, input.me),
    updates,
    toReview: updates.filter(isUpdateToReview),
    lastChecked: input.updates?.lastCheckedAt ?? null,
    allowance: homeAllowance(input.spend),
    workflows: input.workflows,
    failing: input.workflows.filter(isWorkflowFailing),
  };
}
